import React, { useEffect, useRef } from "react";
import SoftwareSkill from "components/softwareSkills/SoftwareSkill";
import { skills } from "portfolio";
import FullStackImg from "./FullStackImg";
import CloudInfraImg from "./CloudInfraImg";
import DesignImg from "./DesignImg";
import type { SkillData, Theme } from "types";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface GetSkillSvgProps {
  fileName: string;
  theme: Theme;
}

function GetSkillSvg(props: GetSkillSvgProps): React.ReactNode {
  if (props.fileName === "FullStackImg")
    return <FullStackImg theme={props.theme} />;
  if (props.fileName === "CloudInfraImg")
    return <CloudInfraImg theme={props.theme} />;
  if (props.fileName === "DesignImg") return <DesignImg theme={props.theme} />;
  return null;
}

interface SkillSectionProps {
  theme: Theme;
}

interface LaneMeta {
  ownership: string;
  scope: string;
}

function getLaneMeta(title: string): LaneMeta {
  if (title === "Backend Development") {
    return {
      ownership: "Platform architecture",
      scope: "APIs • Data • Reliability",
    };
  }

  if (title === "Cloud & DevOps") {
    return {
      ownership: "Production operations",
      scope: "Cloud • CI/CD • Observability",
    };
  }

  return {
    ownership: "Product experience",
    scope: "Frontend systems • UX quality",
  };
}

function getSkillProficiency(title: string): number {
  if (title === "Backend Development") return 90;
  if (title === "Cloud & DevOps") return 85;
  return 80; // Frontend and others
}

const SkillSection: React.FC<SkillSectionProps> = (props) => {
  const theme = props.theme;
  const skillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const titleRefs = useRef<(HTMLHeadingElement | null)[]>([]);
  const descRefs = useRef<(HTMLParagraphElement | null)[][]>([]);

  useEffect(() => {
    // Detect mobile devices for reduced animations
    const isMobile = window.innerWidth < 768;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    skillRefs.current.forEach((ref, index) => {
      if (ref) {
        // Simplified animations for mobile/reduced motion
        const mobileConfig = {
          scale: isMobile ? 0.9 : 0.8,
          rotationY: isMobile
            ? index % 2 === 0
              ? -45
              : 45
            : index % 2 === 0
              ? -90
              : 90,
          x: isMobile
            ? index % 2 === 0
              ? -50
              : 50
            : index % 2 === 0
              ? -100
              : 100,
          duration: isMobile ? 0.8 : 1.2,
        };

        gsap.fromTo(
          ref,
          {
            opacity: 0,
            scale: mobileConfig.scale,
            rotationY: reducedMotion ? 0 : mobileConfig.rotationY,
            x: reducedMotion ? 0 : mobileConfig.x,
          },
          {
            opacity: 1,
            scale: 1,
            rotationY: 0,
            x: 0,
            duration: mobileConfig.duration,
            ease: reducedMotion ? "power2.out" : "back.out(1.7)",
            scrollTrigger: {
              trigger: ref,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse",
            },
            onComplete: () => {
              // Skip complex animations on mobile/reduced motion
              if (isMobile || reducedMotion) return;

              // Elastic title bounce
              const title = titleRefs.current[index];
              if (title) {
                gsap.fromTo(
                  title,
                  {
                    opacity: 0,
                    y: -50,
                    scale: 0.5,
                  },
                  {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    ease: "elastic.out(1, 0.5)",
                  },
                );
              }

              // Scale and stagger descriptions
              const descriptions = descRefs.current[index] || [];
              gsap.fromTo(
                descriptions,
                {
                  opacity: 0,
                  y: 30,
                  scale: 0.9,
                },
                {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.7,
                  stagger: 0.15,
                  delay: 0.4,
                  ease: "power3.out",
                },
              );
            },
          },
        );

        // Simplified logo animations for mobile
        const logos = ref.querySelectorAll(".software-skill li");
        const logoConfig = {
          x: isMobile ? -30 : -50,
          y: isMobile ? 10 : 20,
          scale: isMobile ? 0.5 : 0.3,
          rotationY: reducedMotion ? 0 : isMobile ? -90 : -180,
          rotationX: reducedMotion ? 0 : isMobile ? -22.5 : -45,
          duration: isMobile ? 0.6 : 0.8,
          stagger: isMobile ? 0.08 : 0.12,
          delay: isMobile ? 0.6 : 1.2,
          ease: reducedMotion ? "power2.out" : "back.out(2)",
        };

        gsap.fromTo(
          logos,
          {
            opacity: 0,
            x: logoConfig.x,
            y: logoConfig.y,
            scale: logoConfig.scale,
            rotationY: logoConfig.rotationY,
            rotationX: logoConfig.rotationX,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
            rotationY: 0,
            rotationX: 0,
            duration: logoConfig.duration,
            stagger: logoConfig.stagger,
            delay: logoConfig.delay,
            ease: logoConfig.ease,
          },
        );

        // Animate any inline skill progress bars (if present) using data-value attribute
        const progresses = ref.querySelectorAll<HTMLElement>(".skill-progress");
        progresses.forEach((el) => {
          const val = Number(el.dataset.value) || 0;
          gsap.fromTo(
            el,
            { width: "0%" },
            {
              width: `${val}%`,
              duration: 0.9,
              ease: "power3.out",
              scrollTrigger: {
                trigger: el,
                start: "top 92%",
                toggleActions: "play none none reverse",
              },
            },
          );
        });

        // Option C: segmented bars animation
        const segmentsWrapper =
          ref.querySelector<HTMLElement>(".skill-segments");
        const skillData = skills.data[index];
        if (segmentsWrapper) {
          const segs = Array.from(
            segmentsWrapper.querySelectorAll<HTMLElement>(".segment"),
          );
          const totalVal = getSkillProficiency(skillData.title);
          // determine how many segments should fill (based on 5 segments)
          const segmentsToFill = Math.round((totalVal / 100) * segs.length);
          gsap.fromTo(
            segs,
            { scaleX: 0 },
            {
              scaleX: (i: number) => (i < segmentsToFill ? 1 : 0.02),
              transformOrigin: "left center",
              duration: 0.8,
              ease: "power3.out",
              stagger: 0.08,
              scrollTrigger: {
                trigger: segmentsWrapper,
                start: "top 92%",
                toggleActions: "play none none reverse",
              },
            },
          );
        }
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);
  return (
    <div className="px-0 sm:px-1 md:px-2">
      {skills.data.map((skill: SkillData, index: number) => {
        // Initialize description refs for this skill
        if (!descRefs.current[index]) {
          descRefs.current[index] = [];
        }
        const isEven = index % 2 === 0;
        const laneMeta = getLaneMeta(skill.title);

        return (
          <div
            ref={(el) => {
              skillRefs.current[index] = el;
            }}
            className="mb-6 rounded-2xl border border-border/60 bg-gradient-to-br from-bg-primary/85 via-bg-primary/70 to-bg-elevated/35 p-3.5 md:mb-7 md:p-4 lg:p-5"
            key={skill.title + index}
          >
            <div className="flex items-center justify-between gap-2 pb-3 mb-4 border-b border-border/60">
              <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-text-muted">
                Capability Lane {String(index + 1).padStart(2, "0")}
              </span>
              <span className="rounded-full border border-border/60 bg-bg-elevated/35 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] text-text-secondary">
                {laneMeta.ownership}
              </span>
            </div>

            <div className="grid items-center gap-4 md:grid-cols-2 md:gap-5">
              <div
                className={`skill-visual flex items-center justify-center rounded-xl border border-border/60 bg-bg-elevated/30 p-3 md:p-4 ${
                  isEven ? "md:order-1" : "md:order-2"
                }`}
              >
                <div className="w-full max-w-md [&>*]:mx-auto [&>*]:h-auto [&>*]:max-w-full">
                  <GetSkillSvg fileName={skill.fileName} theme={theme} />
                </div>
              </div>

              <div
                className={`skill-copy font-sans ${
                  isEven ? "md:order-2" : "md:order-1"
                }`}
              >
                <h1
                  ref={(el) => {
                    titleRefs.current[index] = el;
                  }}
                  className="text-xl font-bold tracking-tight md:text-2xl lg:text-3xl font-display text-text-primary max-md:text-center"
                >
                  {skill.title}
                </h1>

                <div className="mt-3 mb-3 max-w-sm mx-auto md:mx-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="w-full max-w-sm">
                      <div className="skill-progress-track bg-border h-2 rounded-full overflow-hidden">
                        <div
                          className="skill-progress h-2 bg-accent"
                          data-value={getSkillProficiency(skill.title)}
                          style={{ width: "0%" }}
                        />
                      </div>
                    </div>
                    <div className="w-24 text-right text-xs text-text-muted">
                      {getSkillProficiency(skill.title)}%
                    </div>
                  </div>
                  {/* Option C: segmented bar with hover tooltips */}
                  <div className="mt-3 skill-segments-wrapper max-w-sm mx-auto md:mx-0">
                    <div className="flex items-center justify-end gap-3">
                      <div className="w-3/4">
                        <div className="skill-segments flex gap-1">
                          {Array.from({ length: 5 }).map((_, segIdx) => {
                            const segVal = (segIdx + 1) * 20; // 5 segments = 20% each
                            const example = String(
                              skill.softwareSkills?.[segIdx] ??
                                skill.skills?.[0] ??
                                "Examples",
                            );
                            return (
                              <div key={segIdx} className="relative group">
                                <div
                                  className={`segment h-3 rounded-full bg-border/40 transform origin-left scale-x-0`}
                                  data-target={segVal}
                                  aria-hidden
                                />
                                <div className="absolute left-1/2 -translate-x-1/2 top-0 mt-6 hidden rounded-md bg-bg-elevated/95 px-2 py-1 text-xs text-text-primary shadow-md group-hover:block">
                                  {example}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="w-1/4 text-right text-xs text-text-muted">
                        Segmented
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex mt-3 mb-4 software-skill max-md:justify-center">
                  <SoftwareSkill logos={skill.softwareSkills} />
                </div>

                <div className="mb-3 flex flex-wrap gap-1.5 max-md:justify-center">
                  <span className="rounded-full border border-border/60 bg-bg-elevated/35 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] text-text-secondary">
                    {laneMeta.scope}
                  </span>
                  <span className="rounded-full border border-border/60 bg-bg-elevated/35 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] text-text-secondary">
                    Technical lead
                  </span>
                </div>

                <div className="space-y-2.5">
                  {skill.skills.map((skillSentence: string, i: number) => {
                    return (
                      <p
                        key={i}
                        ref={(el) => {
                          if (el) descRefs.current[index][i] = el;
                        }}
                        className="subTitle rounded-r-md border-l-2 border-accent/60 bg-bg-elevated/20 px-2.5 py-1.5 text-sm leading-relaxed text-text-secondary md:text-base max-md:text-center"
                      >
                        {skillSentence}
                      </p>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SkillSection;
